import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { dehydrate, HydrationBoundary } from "@tanstack/react-query";
import { AppSidebar } from "@/components/sidebar/sidebar";
import TopNavigation from "@/components/top-navigation";
import { SidebarProvider } from "@/components/ui/sidebar";
import { fetchStatus } from "@/server/status";
import { Toaster } from "@/components/ui/toaster";
import { settingTypesQueryOptions, systemSettingsQueryOptions } from "@/server/settings";
import pkg from "../../../package.json";
import { getEnumDefinitionsQueryOptions } from "@/server/enums";
import { toast } from "sonner";

const packageVersion = pkg.version + "-" + pkg.config.release;

export const Route = createFileRoute("/_dashboard")({
  component: AppLayoutComponent,
  ssr: true,
  loader: async ({ context, location }) => {
    if (!context.user) {
      throw redirect({
        to: "/login",
        search: { redirect: location.href },
      });
    }

    try {
      await context.queryClient.prefetchQuery(settingTypesQueryOptions);
      await context.queryClient.prefetchQuery(systemSettingsQueryOptions);
    } catch (error) {
      toast.error(error.message || "Failed to fetch settings. Please try again later.");
    }

    await context.queryClient.prefetchQuery(getEnumDefinitionsQueryOptions);

    return {
      status: await fetchStatus(),
      user: context.user,
      dehydratedState: dehydrate(context.queryClient) as any,
    };
  },
  errorComponent: ({ error }) => {
    throw error;
  },
});

function AppLayoutComponent() {
  const { status, user } = Route.useLoaderData();
  const { dehydratedState } = Route.useLoaderData();

  return (
    <HydrationBoundary state={dehydratedState}>
      <SidebarProvider>
        <AppSidebar user={user} />
        <div className="flex flex-col w-full h-screen overflow-hidden">
          <TopNavigation status={status} packageVersion={packageVersion} />
          <div className="flex mmx-wrapper flex-1 min-h-0">
            <Outlet />
          </div>
        </div>
        <Toaster position="top-center" richColors />
      </SidebarProvider>
    </HydrationBoundary>
  );
}
